"use client";

import { c as _c } from "react/compiler-runtime";
import type { GetSectionType } from "page";
import DirectionalButton from "app/components/DirectionalButton";
import Kicker from "app/components/Kicker";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import { getSanityDataAttribute } from "library/sanity/getSanityDataAttribute";
import { useAnimation } from "library/useAnimation";
import { getBreakpoint, getResponsivePixels } from "library/viewportUtils";
import { useRef, useState } from "react";
import TestimonialCard from "./TestimonialCard";
import { Wrapper, Content, Heading, KickerRow, Headline, DescriptionRow, Description, Directions, CarouselViewport, Track } from "data:text/javascript;base64,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";
const mod = (n: number, m: number) => (n % m + m) % m;
const TESTIMONIAL_SLIDE_EASE = "cubic-bezier(0.8, 0, 0.3, 1)";
const MAIN_EASE = 0.8;
export default function TestimonialsSection(t0) {
  const $ = _c(29);
  const {
    kickerText,
    headline,
    description,
    testimonials,
    headerMode,
    sanityDataAttribute
  } = t0;
  const [activeIndex, setActiveIndex] = useState(0);
  const [sectionInView, setSectionInView] = useState(true);
  const isSlidingRef = useRef(false);
  const activeIndexRef = useRef(activeIndex);
  const cardRefs = useRef([]);
  const contentRefs = useRef([]);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const videoRefs = useRef(t1);
  const wrapperRef = useRef(null);
  const userPausedRef = useRef(false);
  const initializedRef = useRef(new Set());
  const count = testimonials?.length ?? 0;
  const attemptPlay = index => {
    if (userPausedRef.current) {
      return;
    }
    const videoEl = videoRefs.current[index];
    if (!videoEl || index !== activeIndexRef.current) {
      return;
    }
    videoEl.play().catch(() => {
      setTimeout(() => attemptPlay(index), 250);
    });
  };
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el = wrapperRef.current;
      if (!el) {
        return;
      }
      const trigger = ScrollTrigger.create({
        trigger: el,
        start: "top bottom",
        end: "bottom top",
        onEnter: () => setSectionInView(true),
        onEnterBack: () => setSectionInView(true),
        onLeave: () => setSectionInView(false),
        onLeaveBack: () => setSectionInView(false)
      });
      return () => trigger.kill();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useAnimation(t2, t3);
  let t4;
  if ($[3] !== activeIndex || $[4] !== sectionInView) {
    t4 = [sectionInView, activeIndex];
    $[3] = activeIndex;
    $[4] = sectionInView;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      updateBehavior: "none"
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useAnimation(() => {
    if (sectionInView && !userPausedRef.current) {
      attemptPlay(activeIndex);
    } else {
      videoRefs.current[activeIndex]?.pause();
    }
  }, t4, t5);
  if (!testimonials || testimonials.length === 0) {
    return null;
  }
  const getStep = () => {
    const firstCard = cardRefs.current[0];
    if (!firstCard) {
      return 0;
    }
    const gapDesignPx = getBreakpoint() === "mobile" ? 12 : 21;
    return firstCard.getBoundingClientRect().width + getResponsivePixels(gapDesignPx);
  };
  const animateCardState = (timeline, index_0, x, isEnteringActive) => {
    const card = cardRefs.current[index_0];
    const content = contentRefs.current[index_0];
    if (card) {
      timeline.to(card, {
        x,
        opacity: isEnteringActive ? 1 : 0.5,
        duration: MAIN_EASE,
        ease: TESTIMONIAL_SLIDE_EASE
      }, 0);
    }
    if (content) {
      timeline.to(content, {
        opacity: isEnteringActive ? 1 : 0,
        pointerEvents: isEnteringActive ? "auto" : "none",
        duration: MAIN_EASE,
        ease: TESTIMONIAL_SLIDE_EASE
      }, 0);
    }
  };
  const move = direction => {
    if (isSlidingRef.current) {
      return;
    }
    const step = getStep();
    if (!step) {
      return;
    }
    isSlidingRef.current = true;
    const previousActiveIndex = activeIndex;
    const peekingIndex = mod(previousActiveIndex + 1, count);
    const nextActiveIndex = mod(previousActiveIndex + direction, count);
    activeIndexRef.current = nextActiveIndex;
    userPausedRef.current = false;
    videoRefs.current[previousActiveIndex]?.pause();
    requestAnimationFrame(() => {
      const timeline_0 = gsap.timeline({
        onComplete: () => {
          isSlidingRef.current = false;
          attemptPlay(nextActiveIndex);
        }
      });
      if (direction === 1) {
        const enteringIndex = mod(peekingIndex + 1, count);
        const enteringCard = cardRefs.current[enteringIndex];
        if (enteringCard) {
          gsap.set(enteringCard, {
            x: 2 * step,
            opacity: 0.5
          });
        }
        animateCardState(timeline_0, previousActiveIndex, -step, false);
        animateCardState(timeline_0, peekingIndex, 0, true);
        animateCardState(timeline_0, enteringIndex, step, false);
      } else {
        const enteringIndex_0 = mod(previousActiveIndex - 1, count);
        const enteringCard_0 = cardRefs.current[enteringIndex_0];
        if (enteringCard_0) {
          gsap.set(enteringCard_0, {
            x: -step,
            opacity: 0.5
          });
        }
        animateCardState(timeline_0, peekingIndex, 2 * step, false);
        animateCardState(timeline_0, previousActiveIndex, step, false);
        animateCardState(timeline_0, enteringIndex_0, 0, true);
      }
    });
    setActiveIndex(nextActiveIndex);
  };
  const registerCard = (index_1, el_0) => {
    cardRefs.current[index_1] = el_0;
    if (!el_0 || initializedRef.current.has(index_1)) {
      return;
    }
    initializedRef.current.add(index_1);
    const step_0 = getStep();
    if (!step_0) {
      return;
    }
    const peekingIndex_0 = mod(activeIndex + 1, count);
    if (index_1 === activeIndex) {
      gsap.set(el_0, {
        x: 0,
        opacity: 1
      });
    } else {
      if (index_1 === peekingIndex_0) {
        gsap.set(el_0, {
          x: step_0,
          opacity: 0.5
        });
      } else {
        gsap.set(el_0, {
          x: 2 * step_0,
          opacity: 0.5
        });
      }
    }
  };
  const registerContent = (index_2, el_1) => {
    contentRefs.current[index_2] = el_1;
    if (!el_1 || initializedRef.current.has(index_2)) {
      return;
    }
    gsap.set(el_1, {
      opacity: index_2 === activeIndex ? 1 : 0,
      pointerEvents: index_2 === activeIndex ? "auto" : "none"
    });
  };
  let t6;
  if ($[7] !== kickerText || $[8] !== sanityDataAttribute) {
    t6 = kickerText && <div data-sanity={getSanityDataAttribute(sanityDataAttribute, "kickerText")}><Kicker variant="dark">{kickerText}</Kicker></div>;
    $[7] = kickerText;
    $[8] = sanityDataAttribute;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <KickerRow>{t6}</KickerRow>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== sanityDataAttribute) {
    t8 = getSanityDataAttribute(sanityDataAttribute, "headline");
    $[12] = sanityDataAttribute;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== headline || $[15] !== t8) {
    t9 = <Headline data-sanity={t8}>{headline}</Headline>;
    $[14] = headline;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== description || $[18] !== sanityDataAttribute) {
    t10 = description && <Description data-sanity={getSanityDataAttribute(sanityDataAttribute, "description")}>{description}</Description>;
    $[17] = description;
    $[18] = sanityDataAttribute;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const T0 = CarouselViewport;
  let t11;
  if ($[20] !== sanityDataAttribute) {
    t11 = getSanityDataAttribute(sanityDataAttribute, "testimonials");
    $[20] = sanityDataAttribute;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const T1 = Track;
  const t12 = testimonials.map((testimonial, index_3) => <TestimonialCard key={testimonial._key} testimonial={testimonial} isActive={index_3 === activeIndex} cardRef={el_2 => registerCard(index_3, el_2)} contentRef={el_3 => registerContent(index_3, el_3)} videoRef={el_4 => {
    videoRefs.current[index_3] = el_4;
  }} onAdvance={() => move(1)} onUserPausedChange={paused => {
    userPausedRef.current = paused;
  }} />);
  let t13;
  if ($[22] !== T1 || $[23] !== t12) {
    t13 = <T1>{t12}</T1>;
    $[22] = T1;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== T0 || $[26] !== t11 || $[27] !== t13) {
    t14 = <T0 data-sanity={t11}>{t13}</T0>;
    $[25] = T0;
    $[26] = t11;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return <Wrapper ref={wrapperRef} data-header-mode={headerMode}><Content><Heading>{t7}{t9}<DescriptionRow>{t10}<Directions><BackArrowButton onClick={() => move(-1)} /><ForwardArrowButton onClick={() => move(1)} /></Directions></DescriptionRow></Heading>{t14}</Content></Wrapper>;
}
function BackArrowButton(t0) {
  const $ = _c(2);
  const {
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = <DirectionalButton type="button" direction="left" onClick={onClick} />;
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ForwardArrowButton(t0) {
  const $ = _c(2);
  const {
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = <DirectionalButton type="button" direction="right" onClick={onClick} />;
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}